import type { ConstraintLevelOption, OptimizePlanConfig } from '../../../config/optimizePlanConfig';
import type {
    ConstraintAttributeRow,
    ConstraintCatalogEntry,
    ConstraintGroup,
    ConstraintInputValue,
    ConstraintLevelBlock,
    ConstraintLevelStatus,
    ConstraintSelection,
    Level,
} from '../components/OptimizePlan/types';
import type { LevelValues, ConstraintValuesStatus } from '../hooks/queries/useConstraintOriginalValues';
import { buildRowFields, computeDefaultRange, isInputInvalid } from './optimizeConstraintHelpers';

/** Identifies one attribute row's input override. */
export const constraintRowKey = (constraintName: string, level: Level, attributeName: string): string =>
    `${constraintName}::${level}::${attributeName}`;

export type LevelOptionWithState = ConstraintLevelOption & { disabled?: boolean };

interface BuildConstraintGroupsParams {
    selections: ConstraintSelection[];
    config: OptimizePlanConfig;
    /** The constraint list from the original-values response. */
    catalog: ConstraintCatalogEntry[];
    levelOptions: LevelOptionWithState[];
    /** Baselines for the scoped level and every level below it. */
    byLevel: Map<Level, LevelValues>;
    /** One status for the whole tab: a single request backs every block. */
    valuesStatus: ConstraintValuesStatus;
    /** User-typed values only; every other cell falls back to the computed default. */
    inputs: Map<string, ConstraintInputValue>;
}

/**
 * One request backs every block, so loading and error are tab-wide. `empty` is the
 * only per-block outcome: the level came back without rows for this KPI, or the
 * user deleted the ones it did have.
 */
function resolveStatus(valuesStatus: ConstraintValuesStatus, visibleRowCount: number): ConstraintLevelStatus {
    if (valuesStatus === 'loading') return 'loading';
    if (valuesStatus === 'error') return 'error';
    return visibleRowCount > 0 ? 'ready' : 'empty';
}

/**
 * Projects the user's selections onto the fetched baselines to produce exactly what
 * the Constraints tab renders and what `run-optimisation` submits.
 *
 * Pure and rebuilt every render rather than mirrored into state, so a refetch after
 * a scope change can never leave a stale row on screen. The only state behind this
 * is the user's own intent: which KPIs, which levels, which rows they deleted, and
 * which values they typed.
 */
export function buildConstraintGroups({
    selections,
    config,
    catalog,
    levelOptions,
    byLevel,
    valuesStatus,
    inputs,
}: BuildConstraintGroupsParams): ConstraintGroup[] {
    const catalogByKey = new Map(catalog.map((entry) => [entry.key, entry]));
    const optionByKey = new Map(levelOptions.map((option) => [option.key, option]));

    const dataColumns = config.constraintTableHeaders.filter((c) => c.type !== 'label' && c.type !== 'input');
    const { constraintDefaultDeltaPercent, constraintDefaultDeltaSourceKey } = config;

    const groups: ConstraintGroup[] = [];

    for (const selection of selections) {
        const catalogEntry = catalogByKey.get(selection.name);
        if (!catalogEntry) continue;

        const usedLevels = new Set<Level>();
        const blocks: ConstraintLevelBlock[] = [];

        for (const levelSelection of selection.levels) {
            const option = optionByKey.get(levelSelection.level);
            // Dropped rather than rendered when the Scope tab has since made this level
            // coarser than the floor allows, so an out-of-scope level is never submitted.
            if (!option || option.disabled) continue;

            usedLevels.add(levelSelection.level);

            const attributes = byLevel.get(levelSelection.level)?.get(selection.name) ?? [];
            const removed = new Set(levelSelection.removedAttributes);

            const rows: ConstraintAttributeRow[] = [];
            for (const attribute of attributes) {
                if (removed.has(attribute.attribute_name)) continue;

                const fields = buildRowFields(attribute as unknown as Record<string, unknown>, dataColumns);
                const defaults = computeDefaultRange(
                    fields[constraintDefaultDeltaSourceKey],
                    constraintDefaultDeltaPercent,
                );
                const rowKey = constraintRowKey(selection.name, levelSelection.level, attribute.attribute_name);
                const input = inputs.get(rowKey) ?? {
                    min: defaults.min_value,
                    max: defaults.max_value,
                    single: null,
                };

                rows.push({
                    attributeName: attribute.attribute_name,
                    fields,
                    input,
                    hasInvalidInput: isInputInvalid(input, catalogEntry.inputType),
                });
            }

            blocks.push({
                level: levelSelection.level,
                levelLabel: option.label,
                status: resolveStatus(valuesStatus, rows.length),
                rows,
            });
        }

        groups.push({
            name: selection.name,
            label: catalogEntry.label,
            inputType: catalogEntry.inputType,
            format: catalogEntry.format,
            levels: blocks,
            addableLevels: levelOptions
                .filter((option) => !usedLevels.has(option.key))
                .map((option) => ({ key: option.key, label: option.label, disabled: option.disabled ?? false })),
        });
    }

    return groups;
}

/**
 * The v3 gate for Run Optimisation.
 *
 * Constraints are optional — objective and start date are the only mandatory
 * inputs, so an empty selection runs unconstrained. But a KPI the user did select
 * must be complete: it needs a level, every level needs a row, and every row needs
 * a complete, non-inverted input, so no null bound ever reaches the backend.
 */
export function areConstraintGroupsSubmittable(groups: ConstraintGroup[]): boolean {
    for (const group of groups) {
        if (group.levels.length === 0) return false;
        for (const block of group.levels) {
            if (block.status !== 'ready' || block.rows.length === 0) return false;
            for (const row of block.rows) {
                if (row.hasInvalidInput) return false;
            }
        }
    }
    return true;
}
